// GRP "+ Boat": a boat is its own row (Name, SAP, Model) with its QA inspection tasks as rows underneath.
// The form fills the boat from the season's Overview boats with no GRP row yet (the Overview takes each boat's
// Name and MODEL from GRP, so until then it holds only the SAP; the customer and model come from the boat's
// configurator order when it has one), or by hand, and copies the task rows from
// an existing boat of the same kind: an "MK-" boat from the latest MK- boat, any other from the latest
// non-MK boat, the latest with a full set (packages/shared/src/domain/grpBoat.ts). You can pick another boat
// to copy from, or none.
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { defaultTemplateBoat, grpSap, grpTaskTree, grpTemplateBoats, isMkBoat } from '@technohull/shared';
import { useGrpAddBoat, useGrpNewBoats, type SheetGridColumn, type SheetGridRow } from '../features/hooks.js';
import { InlineError, Modal } from '../components/ui.js';

export function GrpAddBoat({ columns, rows, primaryId, onClose, onAdded }: {
  columns: SheetGridColumn[];
  rows: SheetGridRow[];
  primaryId: number;
  onClose: () => void;
  onAdded: (id: number) => void;
}) {
  const add = useGrpAddBoat();
  const sapCol = columns.find((c) => /^sap\b/i.test(c.title.trim()));
  const modelCol = columns.find((c) => /^model\b/i.test(c.title.trim()));

  // the season's boats with no GRP row yet (by SAP, however the sheet stores it)
  const fresh = useGrpNewBoats(true);
  const onSheet = useMemo(
    () => new Map(sapCol ? rows.filter((r) => r.parentId == null).flatMap((r) => { const s = grpSap(r.cells[sapCol.id]); return s ? [[s, r] as const] : []; }) : []),
    [rows, sapCol],
  );
  const candidates = useMemo(() => (fresh.data?.boats ?? []).filter((b) => !onSheet.has(b.sap)), [fresh.data, onSheet]);

  const boats = useMemo(() => grpTemplateBoats(rows, primaryId), [rows, primaryId]);
  const example = boats[boats.length - 1]?.name ?? '';
  const [name, setName] = useState('');
  const [sap, setSap] = useState('');
  const [model, setModel] = useState('');
  const [pick, setPick] = useState('');
  // the boat the tasks are copied from: follows the name (MK- or not) until someone picks one
  const [chosen, setChosen] = useState<number | 'none' | null>(null);
  const auto = defaultTemplateBoat(boats, name);
  const templateId = chosen === 'none' ? null : chosen ?? auto?.id ?? null;
  const template = boats.find((b) => b.id === templateId) ?? null;
  const tree = useMemo(() => (templateId ? grpTaskTree(rows, primaryId, templateId) : []), [rows, primaryId, templateId]);
  const mk = isMkBoat(name);
  const noSameKind = chosen === null && !!name.trim() && !auto;

  // the sheet names a boat by its customer ("MORAITIS (MK-71)"); the SAP and model have their own columns
  const fromFleet = (sapPicked: string) => {
    setPick(sapPicked);
    const v = candidates.find((x) => x.sap === sapPicked);
    if (!v) return;
    setSap(v.sap);
    setModel(v.model);
    if (v.customer) setName(v.customer);
  };
  const picked = candidates.find((x) => x.sap === pick);
  // a SAP already on the sheet: one row per boat (the server refuses it too)
  const twin = grpSap(sap) ? onSheet.get(grpSap(sap)) : undefined;

  const submit = () => {
    const cells = [{ columnId: primaryId, value: name.trim() }];
    if (sapCol && sap.trim()) cells.push({ columnId: sapCol.id, value: sap.trim() });
    if (modelCol && model.trim()) cells.push({ columnId: modelCol.id, value: model.trim() });
    add.mutate({ cells, templateId }, { onSuccess: (r) => { onAdded(r.id); onClose(); } });
  };

  return createPortal(
    <Modal
      title="Add a boat"
      onClose={onClose}
      width={560}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!name.trim() || !!twin || add.isPending} onClick={submit}>
            {add.isPending ? 'Adding…' : template ? `Add boat with ${countLabel(template.tasks)}` : 'Add boat'}
          </button>
        </>
      )}
    >
      <div className="gab">
        {candidates.length > 0 && (
          <label className="gab-field">
            <span>From the season's Overview, not on GRP yet</span>
            <select className="field" value={pick} onChange={(e) => fromFleet(e.currentTarget.value)}>
              <option value="">Choose a boat, or fill in below</option>
              {candidates.map((v) => (
                <option key={v.sap} value={v.sap}>{[v.sap, v.model || v.orderModel, v.customer].filter(Boolean).join(' · ')}</option>
              ))}
            </select>
            {picked && !picked.customer && <small className="muted">No configurator order carries this SAP: type the name as on the sheet.</small>}
            {picked && picked.orderModel && !picked.model && <small className="muted">The order’s model “{picked.orderModel}” isn’t in the sheet’s MODEL list: pick it below.</small>}
          </label>
        )}
        <label className="gab-field">
          <span>Name</span>
          <input className="field" value={name} autoFocus onChange={(e) => setName(e.currentTarget.value)} placeholder={example || 'Boat name'} />
          {example && <small className="muted">As on the sheet, for example “{example}”.</small>}
        </label>
        <div className="gab-row">
          {sapCol && (
            <label className="gab-field">
              <span>SAP</span>
              <input className="field" inputMode="numeric" value={sap} onChange={(e) => setSap(e.currentTarget.value)} aria-invalid={!!twin} />
              {twin && <small className="gab-warn">Already on the sheet as “{String(twin.cells[primaryId] ?? '').trim() || 'a boat'}”.</small>}
            </label>
          )}
          {modelCol && (
            <label className="gab-field">
              <span>Model</span>
              {modelCol.options.length ? (
                <select className="field" value={model} onChange={(e) => setModel(e.currentTarget.value)}>
                  <option value="" />
                  {model && !modelCol.options.includes(model) && <option value={model}>{model}</option>}
                  {modelCol.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : <input className="field" value={model} onChange={(e) => setModel(e.currentTarget.value)} />}
            </label>
          )}
        </div>

        <fieldset className="gab-tasks">
          <legend>QA inspection tasks</legend>
          <label className="gab-field">
            <span>Copy the task rows from</span>
            <select
              className="field"
              value={templateId == null ? 'none' : String(templateId)}
              onChange={(e) => setChosen(e.currentTarget.value === 'none' ? 'none' : Number(e.currentTarget.value))}
            >
              <option value="none">No tasks (an empty boat)</option>
              {(['mk', 'other'] as const).map((kind) => {
                const list = boats.filter((b) => b.mk === (kind === 'mk')).slice().reverse();
                return list.length ? (
                  <optgroup key={kind} label={kind === 'mk' ? 'MK- boats (their own tasks)' : 'Other boats'}>
                    {list.map((b) => <option key={b.id} value={b.id}>{b.name || '(unnamed)'} · {countLabel(b.tasks)}</option>)}
                  </optgroup>
                ) : null;
              })}
            </select>
          </label>
          {noSameKind && (
            <p className="gab-note">
              {mk ? 'There is no MK- boat on the sheet yet to copy the MK- tasks from' : 'There is no other boat on the sheet yet to copy tasks from'}:
              {' '}the boat is added without tasks, or pick a boat above.
            </p>
          )}
          {template && (
            <>
              <p className="muted gab-sum">
                {countLabel(template.tasks)} copied from <b>{template.name || '(unnamed)'}</b>, names only: no results, dates or inspectors.
                {chosen === null && <> Picked because the name {mk ? 'has' : 'has no'} “MK-”.</>}
              </p>
              <ul className="gab-tree">
                {tree.map((t, i) => (
                  <li key={i}>
                    <span>{t.name || '(no name)'}</span>
                    {t.children.length > 0 && <span className="muted"> · {t.children.length}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </fieldset>
        {add.isError && <InlineError error={add.error} onDismiss={() => add.reset()} />}
      </div>
    </Modal>,
    document.body,
  );
}

const countLabel = (n: number) => `${n} task row${n === 1 ? '' : 's'}`;
